// The generic `InventoryMap` grammar instance for Etusivu — a real, zoomable world map of
// per-building circles (ADR-018). Replaces the former fixed Finland silhouette and its seven hardcoded
// city-pin coordinates: a building plots at its own real `lat`/`lon`, never a city-centroid guess,
// and a building with no resolvable coordinate is excluded from the map and counted honestly here
// rather than hidden. Clustering, worst-status cluster color, and building-circle
// click-to-scope wiring all live in the shared `BuildingClusterMap` (also ridden by
// `OverviewMap`) — this component only supplies the €/kk tone/size derivation and panel chrome.
import { MapPin, X, Loader2 } from "lucide-react";
import { useInventoryFilter } from "@/lib/inventory-filter";
import { useFilteredBuildings } from "@/lib/inventory-scope";
import { cardBase } from "@/lib/card-styles";
import { cn } from "@/lib/utils";
import { useTheme } from "@/lib/theme";
import type { Building } from "@/lib/api/building";
import {
  BuildingClusterMap,
  type BuildingCirclePoint,
} from "@/components/map/BuildingClusterMap";
import { fitPointsForScope, inventoryScopeKey } from "@/lib/map-camera";

const STATUS_RANK = { ok: 0, warn: 1, alert: 2 } as const;
// Index-aligned with STATUS_RANK's values (0/1/2) — the shared cluster layer resolves each var
// to a literal color, since MapLibre paint expressions can't read var() from the DOM cascade.
const RANK_COLOR_VARS = [
  "--color-success",
  "--color-warning",
  "--color-destructive",
];

const tone: Record<string, string> = {
  ok: "var(--color-success)",
  warn: "var(--color-warning)",
  alert: "var(--color-destructive)",
};

/** Circle radius in px — mirrors the former pin-size formula's clamp, scaled for a GL circle
 *  marker rather than a DOM pin's CSS width. */
function radiusForImpact(impactEurMonth: number | null): number {
  return Math.max(6, Math.min(22, (impactEurMonth ?? 0) / 900 + 6));
}

function toPoint(
  building: Building,
  activeId: string | null,
): BuildingCirclePoint | null {
  if (building.lat == null || building.lon == null) return null;
  return {
    id: building.id,
    lat: building.lat,
    lon: building.lon,
    rank: STATUS_RANK[building.status],
    radius: radiusForImpact(building.optimization_potential_eur_month),
    active: building.id === activeId,
  };
}

export function InventoryMap() {
  const {
    buildingId,
    groupId,
    filter,
    setBuildingId,
    isFiltered,
    reset,
    buildings,
    inventoryReady,
  } = useInventoryFilter();
  const { mode, palette } = useTheme();

  // Same city/type/status + salkku narrowing every other perspective applies via
  // `useFilteredBuildings` (trend/reports/recommendations/... routes) — the map is a view of the
  // same scoped inventory, not a separate unfiltered one. Rows come from the shared
  // InventoryFilterProvider cache so Hallitse address/coordinate edits appear without a reload.
  const allBuildings = inventoryReady ? buildings : [];
  const scopedBuildings = useFilteredBuildings(allBuildings);
  const points = scopedBuildings
    .map((b) => toPoint(b, buildingId))
    .filter((p): p is BuildingCirclePoint => p !== null);
  const missingCoordCount = scopedBuildings.length - points.length;
  const fitPoints = fitPointsForScope(points, buildingId);
  const scopeKey = inventoryScopeKey({
    buildingId,
    groupId,
    city: filter.city,
    type: filter.type,
    status: filter.status,
  });

  return (
    <div className={cn(cardBase, "p-4 h-full flex flex-col")}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold tracking-tight">
            Kiinteistökannan vaikutuskartta
          </h3>
          <p className="text-[11px] text-muted-foreground">
            Missä optimointi tuottaa juuri nyt eniten — pallon koko = €/kk
            vaikutus
          </p>
        </div>
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <Legend color={tone.ok} label="Optimoitu" />
          <Legend color={tone.warn} label="Potentiaalia" />
          <Legend color={tone.alert} label="Toimi nyt" />
        </div>
      </div>

      <div className="relative flex-1 min-h-[420px] rounded-lg overflow-hidden">
        <BuildingClusterMap
          bare
          className="h-full"
          mapClassName="h-full rounded-lg"
          points={points}
          fitPoints={fitPoints}
          scopeKey={scopeKey}
          emptyFit={buildingId ? "leave" : "overview"}
          rankColorVars={RANK_COLOR_VARS}
          themeKey={`${palette}-${mode}`}
          onSelectBuilding={(id) => {
            if (id === buildingId) return;
            setBuildingId(id);
          }}
        />

        {!inventoryReady && (
          <div className="absolute inset-0 flex items-center justify-center bg-card/50 backdrop-blur-sm rounded-lg pointer-events-none">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 text-[10px] text-muted-foreground bg-card/80 backdrop-blur rounded px-1.5 py-0.5 pointer-events-none">
          <MapPin className="h-3 w-3" />
          {inventoryReady
            ? `${points.length} kohdetta kartalla${
                missingCoordCount > 0
                  ? ` · ${missingCoordCount} kiinteistöä ilman koordinaatteja (Hallitse → sijainti)`
                  : ""
              } · klikkaa palloa rajataksesi`
            : "Ladataan…"}
        </div>
        {isFiltered && (
          <button
            onClick={reset}
            className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-md border border-border bg-card/80 backdrop-blur px-2 py-1 text-[11px] hover:border-primary/40 transition"
          >
            <X className="h-3 w-3" />
            Tyhjennä rajaus
          </button>
        )}
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      {label}
    </span>
  );
}
